import { usdFormatter } from '~configs';
import { featureFlagsService } from '~services';

export * from './card.util';
export * from './phone.util';

export function iOS() {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

export function isAndroid() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf('android') > -1;
}

export const getDeepLinkForApp = () =>
  iOS()
    ? process.env.REACT_APP_IOS_DEEP_LINK
    : isAndroid()
    ? process.env.REACT_APP_ANDROID_DEEP_LINK
    : process.env.REACT_APP_CF_MAIN_PAGE_LINK;

export const handleOpenApp = () => {
  window?.open(getDeepLinkForApp());
};

export const handleOpenLinkInNewTab = (url: string) => () => {
  window?.open(url, '_blank', 'noopener noreferrer');
};

export const handleOpenDeepLink = (deep_link?: string) => {
  window?.open(deep_link, '_blank');
};

/**
 * Converts an amount from cents to a formatted USD string.
 * If the amount is null or undefined, returns a placeholder string.
 *
 * @param {number | undefined | null} amount - The amount in cents to be converted.
 *        This can be a number, or null/undefined if the amount is not known.
 * @returns {string} The formatted amount in USD, e.g., "$20", or "$--" if the input is nullish.
 */
export const centToUSD = (amount: number | undefined | null): string => {
  if (amount == null) {
    return '$--';
  }
  return usdFormatter.format(amount / 100);
};

/**
 * Formats a monetary amount (in cents) into a currency string.
 *
 * @param amount - The monetary amount in cents (e.g., 12345 represents $123.45).
 * @param symbol - The currency symbol to prepend (e.g., '$', '€'). Defaults to '$' if not provided.
 * @param maximumFractionDigits - 1.00, 2.0
 * @returns A formatted currency string. Returns '--' if the amount is undefined, null, or zero.
 */
export const formatCurrency = (
  amount: number | undefined | null,
  symbol: string | undefined | null,
  maximumFractionDigits = 0,
): string => {
  const currencySymbol = symbol ?? '$';
  if (amount == null) {
    return `${currencySymbol}--`;
  }
  const amountInDollars = parseFloat((amount / 100).toString());
  const amountDisplayString = maximumFractionDigits
    ? amountInDollars.toFixed(maximumFractionDigits)
    : amountInDollars;
  return `${currencySymbol}${amountDisplayString}`;
};

export function checkEnabledFeatureFlag(key: string) {
  const featureFlags = featureFlagsService.getFeatureFlags();
  const featureFlag = featureFlags.find(
    (featureFlag) => featureFlag.key === key,
  );
  return !!featureFlag?.enabled;
}

export function checkEnabledFeatureFlags(keys: string[]) {
  return keys.every((flag) => checkEnabledFeatureFlag(flag));
}

/**
 * Properly capitalize a card brand name
 *
 * @param input The card brand name to capitalize
 * @returns The properly capitalized card brand name
 */
export const properCardBrandName = (input: string) => {
  switch (input.trim().toLowerCase()) {
    case 'amex':
      return 'Amex';
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'Mastercard';
    case 'discover':
      return 'Discover';
    case 'diners':
      return 'Diners';
    case 'jcb':
      return 'JCB';
    case 'unionpay':
      return 'UnionPay';
    default:
      return 'unknown';
  }
};

/**
 * Get localization key for a time unit
 *
 * @param unit The time unit to get the localization key for
 * @returns The localization key for the time unit
 */
export const getTimeUnitLocaleKey = (unit: string): string => {
  switch (unit) {
    case 'second':
      return 'text.timeUnit.second';
    case 'seconds':
      return 'text.timeUnit.seconds';
    case 'min':
      return 'text.timeUnitShort.minute';
    case 'minute':
      return 'text.timeUnit.minute';
    case 'mins':
      return 'text.timeUnitShort.minutes';
    case 'minutes':
      return 'text.timeUnit.minutes';
    case 'hour':
      return 'text.timeUnit.hour';
    case 'hours':
      return 'text.timeUnit.hours';
    case 'day':
      return 'text.timeUnit.day';
    case 'days':
      return 'text.timeUnit.days';
    default:
      return 'text.timeUnit.second';
  }
};

/**
 * Formats pricing in a user-friendly way
 * - Less than 60 minutes: "/x min(s)"
 * - Exactly 60 minutes or 1 hour: "/hour"
 * - Exact hours greater than 1 hour: "for x hours"
 * - Non-whole hours: "for x hour(s) y min(s)"
 * - A single day: "/day"
 * - Multiple days: "for x days"
 *
 * @param unitQuantity The quantity of the time unit
 * @param unit The time unit (minutes, hours, days)
 * @param lt The localization function to get localized strings
 * @returns The formatted pricing string based on the unit and quantity
 */
export const formatPricing = (
  unitQuantity: number,
  unit: string,
  lt: (
    key: string,
    fallback: string,
    values?: Record<string, string | number>,
  ) => string,
): string => {
  const localize = (u: string) => lt(getTimeUnitLocaleKey(u), u);
  const lowerUnit = unit.toLowerCase();

  // Handle min/mins
  if (lowerUnit === 'minutes') {
    if (unitQuantity < 60) {
      // Less than an hour: show "/min(s)"
      return `/${unitQuantity} ${localize(
        unitQuantity === 1 ? 'min' : 'mins',
      )}`;
    } else if (unitQuantity === 60) {
      // Exactly 60 minutes: show "/hour"
      return `/${localize('hour')}`;
    } else {
      // More than 60 minutes: convert to hours and use "for"
      const hours = Math.floor(unitQuantity / 60);
      const minutes = Math.round(unitQuantity % 60);

      if (minutes === 0) {
        // exact hour(s)
        return lt(
          'text.pricingFormat.forQuantityUnit',
          `for ${hours} ${localize(hours === 1 ? 'hour' : 'hours')}`,
          {
            quantity: hours,
            unit: localize(hours === 1 ? 'hour' : 'hours'),
          },
        );
      }

      // non-whole hours
      return lt(
        'text.pricingFormat.forHourMinute',
        `for ${hours} ${localize(
          hours === 1 ? 'hour' : 'hours',
        )} ${minutes} ${localize('mins')}`,
        {
          hours,
          minutes,
          hourUnit: localize(hours === 1 ? 'hour' : 'hours'),
          minuteUnit: localize(minutes === 1 ? 'min' : 'mins'),
        },
      );
    }
  }

  // Handle hour/hours
  if (lowerUnit === 'hours') {
    if (unitQuantity === 1) {
      return `/${localize('hour')}`;
    }

    return lt(
      'text.pricingFormat.forQuantityUnit',
      `for ${unitQuantity} ${localize('hours')}`,
      {
        quantity: unitQuantity,
        unit: localize('hours'),
      },
    );
  }

  // Handle day/days
  if (lowerUnit === 'days') {
    if (unitQuantity === 1) {
      return `/${localize('day')}`;
    }

    return lt(
      'text.pricingFormat.forQuantityUnit',
      `for ${unitQuantity} ${localize('days')}`,
      {
        quantity: unitQuantity,
        unit: localize('days'),
      },
    );
  }

  // Fallback. `localize` resolves an unknown unit to a safe default, so the
  // raw unit is passed through as-is rather than being sliced to guess a
  // singular form.
  return `/${unitQuantity} ${localize(lowerUnit)}`;
};
